import invariant from "tiny-invariant";

import CustomFieldDataType from "@mapmycustomers/shared/enum/CustomFieldDataType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import CombineOperator from "@mapmycustomers/shared/types/viewModel/internalModel/CombineOperator";
import {
  AreaFilterCondition,
  CombinedCondition,
  FilterModel,
  LegacySimpleCondition,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformCombineOperator from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformCombineOperator";
import PlatformFilterModel, {
  PlatformCombinedCondition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import { getLocalTimeZone } from "@app/util/dates";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import CustomField from "@app/util/fieldModel/impl/CustomField";
import { isEntityValue } from "@app/util/filters/Location/assert";
import { formatRawDate } from "@app/util/formatters";
import { isCombinedCondition, isSimplePlatformCondition } from "@app/util/viewModel/assert";
import { getFieldByFilterName } from "@app/util/viewModel/getFieldByFilterName";
import { isMetaField } from "@app/util/viewModel/util";

import IntervalUnit from "../../enum/IntervalUnit";

import { dateOperators, SET_FILTER_OPERATOR, timeOperators } from "./const";

export const filterOperatorToPlatformFilterOperatorMap: Partial<{
  [key in FilterOperator]: PlatformFilterOperator;
}> = {
  [FilterOperator.AFTER]: PlatformFilterOperator.GREATER_THAN,
  [FilterOperator.BEFORE]: PlatformFilterOperator.LESS_THAN,
  [FilterOperator.CONTAINS]: PlatformFilterOperator.CONTAINS,
  [FilterOperator.DOES_NOT_END_WITH]: PlatformFilterOperator.DOES_NOT_END_WITH,
  [FilterOperator.DOES_NOT_START_WITH]: PlatformFilterOperator.DOES_NOT_START_WITH,
  [FilterOperator.ENDS_WITH]: PlatformFilterOperator.ENDS_WITH,
  [FilterOperator.EQUALS]: PlatformFilterOperator.EQUALS,
  [FilterOperator.GREATER_THAN]: PlatformFilterOperator.GREATER_THAN,
  [FilterOperator.GREATER_THAN_OR_EQUAL]: PlatformFilterOperator.GREATER_THAN_OR_EQUAL,
  [FilterOperator.LESS_THAN]: PlatformFilterOperator.LESS_THAN,
  [FilterOperator.LESS_THAN_OR_EQUAL]: PlatformFilterOperator.LESS_THAN_OR_EQUAL,
  [FilterOperator.NONE_OF]: PlatformFilterOperator.NOT_CONTAINS,
  [FilterOperator.NOT_CONTAINS]: PlatformFilterOperator.NOT_CONTAINS,
  [FilterOperator.NOT_EQUAL]: PlatformFilterOperator.NOT_EQUAL,
  [FilterOperator.NOT_IN]: PlatformFilterOperator.NOT_CONTAINS,
  [FilterOperator.NOT_ON]: PlatformFilterOperator.NOT_EQUAL,
  [FilterOperator.ON]: PlatformFilterOperator.EQUALS,
  [FilterOperator.ON_OR_AFTER]: PlatformFilterOperator.GREATER_THAN_OR_EQUAL,
  [FilterOperator.ON_OR_BEFORE]: PlatformFilterOperator.LESS_THAN_OR_EQUAL,
  [FilterOperator.STARTS_WITH]: PlatformFilterOperator.STARTS_WITH,
} as const;

export const intervalUnitToRound: Record<IntervalUnit, string> = {
  [IntervalUnit.DAY]: "days",
  [IntervalUnit.HOUR]: "hours",
  [IntervalUnit.MINUTE]: "minutes",
  [IntervalUnit.MONTH]: "months",
  [IntervalUnit.WEEK]: "weeks",
  [IntervalUnit.YEAR]: "years",
} as const;

export const filterCombineOperatorToPlatformCombineOperator: {
  [key in CombineOperator]: PlatformCombineOperator;
} = {
  AND: "$and",
  NOT: "$not",
  OR: "$or",
} as const;

const getIntervalCondition = (condition: LegacySimpleCondition): PlatformFilterCondition => {
  const unit = intervalUnitToRound[condition.value!.unit as IntervalUnit];
  if (
    condition.operator === FilterOperator.INTERVAL_WITHIN_LAST ||
    condition.operator === FilterOperator.INTERVAL_WITHIN_NEXT
  ) {
    return {
      [PlatformFilterOperator.INTERVAL]: {
        $round: unit,
        [condition.operator === FilterOperator.INTERVAL_WITHIN_LAST
          ? PlatformFilterOperator.GREATER_THAN
          : PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: `${condition.value!.from} ${unit}`,
        [condition.operator === FilterOperator.INTERVAL_WITHIN_LAST
          ? PlatformFilterOperator.LESS_THAN_OR_EQUAL
          : PlatformFilterOperator.LESS_THAN]: `${condition.value!.to} ${unit}`,
      },
    };
  } else if (condition.operator === FilterOperator.INTERVAL_AFTER) {
    return {
      [PlatformFilterOperator.INTERVAL]: {
        $round: unit,
        [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: `${condition.value!.value} ${unit}`,
      },
    };
  } else if (condition.operator === FilterOperator.INTERVAL_BEFORE) {
    return {
      [PlatformFilterOperator.INTERVAL]: {
        $round: unit,
        [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: `${condition.value!.value} ${unit}`,
      },
    };
  } else {
    // impossible case, but we need to return something
    return {};
  }
};

export const getRegularFieldPlatformConditionValue = (
  field: IField,
  condition: LegacySimpleCondition
) => {
  const isDateField = field.type === FieldType.DATE || field.type === FieldType.DATE_TIME;

  // For date fields we ignore time part, but using the TZ to guarantee correct results.
  // First, convert it to the correct date, for that we use formatRawDate instead of
  // substring(0, 10) because in some cases it leaded to incorrect results.
  // E.g. it is 2023-10-16T21:07:00-04:00 locally, which is
  // 2023-10-17T01:07:00Z. And if user expected Oct 16, in reality they'll get Oct 17,
  // which is not correct. Hence, we format the date to get a day for local TZ.
  //
  // As one could notice we only have date operators here, so if one uses LESS_THAN,
  // that will not be converted to a day-date and will be used as is.
  if (isDateField && dateOperators.has(condition.operator)) {
    switch (condition.operator) {
      case FilterOperator.IN_RANGE:
        return {
          $timezone: getLocalTimeZone(),
          [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: formatRawDate(
            condition.value[0],
            "yyyy-MM-dd"
          ),
          [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: formatRawDate(
            condition.value[1],
            "yyyy-MM-dd"
          ),
        };
      default: {
        const platformOperator = filterOperatorToPlatformFilterOperatorMap[condition.operator];
        invariant(
          platformOperator,
          `Failed to convert "${condition.operator}" into platform operator`
        );
        return {
          $timezone: getLocalTimeZone(),
          [platformOperator]: formatRawDate(condition.value, "yyyy-MM-dd"),
        };
      }
    }
  }

  switch (condition.operator) {
    case FilterOperator.IN_RANGE:
      return {
        [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: condition.value[0],
        [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: condition.value[1],
      };
    case FilterOperator.IN_ALL:
      return { [PlatformFilterOperator.GROUP_IN_ALL]: condition.value };
    case SET_FILTER_OPERATOR:
      if (Array.isArray(condition.value) && !condition.value.length) {
        return undefined; // do no filtering if array is empty
      }
      if (
        field.hasAnyFeature(
          FieldFeature.GROUP_FIELD,
          FieldFeature.ROUTE_FIELD,
          FieldFeature.TERRITORY_FIELD
        )
      ) {
        return { [PlatformFilterOperator.GROUP_IN_ANY]: condition.value };
      } else {
        return { [PlatformFilterOperator.CONTAINS]: condition.value };
      }
    case FilterOperator.IN_AREA: {
      const areaFilter = condition as AreaFilterCondition;
      if (isEntityValue(areaFilter.value)) {
        return {
          entity: areaFilter.value.entity,
          radius: areaFilter.value.distanceInMeters,
        };
      } else {
        return {
          location: areaFilter.value.location,
          point: [areaFilter.value.longitude, areaFilter.value.latitude],
          radius: areaFilter.value.distanceInMeters,
          uom: "meter", // radius is in meters
          ...(field.platformAreaFieldName ? { fieldName: field.platformAreaFieldName } : {}),
        };
      }
    }
    case FilterOperator.EMPTY:
      return { [PlatformFilterOperator.EQUALS]: null };
    case FilterOperator.NOT_EMPTY:
      return { [PlatformFilterOperator.NOT_EQUAL]: null };
    case FilterOperator.IS_OVERDUE:
      return { [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: 0 };
    case FilterOperator.IS_UPCOMING:
      return { [PlatformFilterOperator.LESS_THAN]: 0 };
    case FilterOperator.IS_OVERDUE_BY_MORE_THAN:
      return { [PlatformFilterOperator.GREATER_THAN]: condition.value };
    case FilterOperator.IS_OVERDUE_BY_LESS_THAN:
      return {
        [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: 0,
        [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: condition.value - 1,
      };
    case FilterOperator.IS_OVERDUE_IN_MORE_THAN:
      return { [PlatformFilterOperator.LESS_THAN]: -condition.value };
    case FilterOperator.IS_OVERDUE_IN_LESS_THAN:
      return {
        [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: -condition.value + 1,
        [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: 0,
      };
    case FilterOperator.IS_TRUE:
      return true;
    case FilterOperator.IS_FALSE:
      return false;
    case FilterOperator.INTERVAL_WITHIN_LAST:
    case FilterOperator.INTERVAL_WITHIN_NEXT:
    case FilterOperator.INTERVAL_AFTER:
    case FilterOperator.INTERVAL_BEFORE:
      return getIntervalCondition(condition);
    // could be under `default` case, but let's make it explicit
    case FilterOperator.EQUALS:
    case FilterOperator.NOT_EQUAL:
    case FilterOperator.LESS_THAN:
    case FilterOperator.GREATER_THAN:
    case FilterOperator.LESS_THAN_OR_EQUAL:
    case FilterOperator.GREATER_THAN_OR_EQUAL:
    case FilterOperator.CONTAINS:
    case FilterOperator.NOT_CONTAINS:
    case FilterOperator.STARTS_WITH:
    case FilterOperator.DOES_NOT_START_WITH:
    case FilterOperator.ENDS_WITH:
    case FilterOperator.DOES_NOT_END_WITH:
    case FilterOperator.NOT_IN:
    case FilterOperator.NONE_OF: {
      const platformOperator = filterOperatorToPlatformFilterOperatorMap[condition.operator];
      invariant(
        platformOperator,
        `Failed to convert "${condition.operator}" into platform operator`
      );
      return { [platformOperator]: condition.value };
    }
  }
};

export const getCustomFieldPlatformConditionValue = (
  field: CustomField,
  condition: LegacySimpleCondition
) => {
  const isDateCustomField =
    isCustomField(field) && field.customFieldData.dataType === CustomFieldDataType.DATE;

  // For date fields we're using the TZ to guarantee correct results.
  // First, convert it to the correct date, for that we use formatRawDate instead of
  // substring(0, 10) because in some cases it leaded to incorrect results.
  // E.g. it is 2023-10-16T21:07:00-04:00 locally, which is
  // 2023-10-17T01:07:00Z. And if user expected Oct 16, in reality they'll get Oct 17,
  // which is not correct. Hence, we format the date to get a day for local TZ.
  //
  // As one could notice we only have date operators here, so if one uses LESS_THAN,
  // that will not be converted to a day-date and will be used as is.
  if (isDateCustomField && dateOperators.has(condition.operator)) {
    switch (condition.operator) {
      case FilterOperator.IN_RANGE:
        return {
          $timezone: getLocalTimeZone(),
          [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: formatRawDate(
            condition.value[0],
            "yyyy-MM-dd"
          ),
          [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: formatRawDate(
            condition.value[1],
            "yyyy-MM-dd"
          ),
        };
      default: {
        const platformOperator = filterOperatorToPlatformFilterOperatorMap[condition.operator];
        invariant(
          platformOperator,
          `Failed to convert "${condition.operator}" into platform operator`
        );
        return {
          $timezone: getLocalTimeZone(),
          [platformOperator]: formatRawDate(condition.value, "yyyy-MM-dd"),
        };
      }
    }
  }

  const isTimeCustomField =
    isCustomField(field) && field.customFieldData.dataType === CustomFieldDataType.TIME;
  if (isTimeCustomField && timeOperators.has(condition.operator)) {
    const platformOperator = filterOperatorToPlatformFilterOperatorMap[condition.operator];
    invariant(platformOperator, `Failed to convert "${condition.operator}" into platform operator`);
    return { [platformOperator]: condition.value };
  }

  switch (condition.operator) {
    case FilterOperator.IN_RANGE:
      return {
        [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: condition.value[0],
        [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: condition.value[1],
      };
    case SET_FILTER_OPERATOR:
      if (Array.isArray(condition.value) && !condition.value.length) {
        return undefined; // do no filtering if array is empty
      }
      return { [PlatformFilterOperator.CONTAINS]: condition.value };
    case FilterOperator.EMPTY:
      return { [PlatformFilterOperator.EQUALS]: null };
    case FilterOperator.NOT_EMPTY:
      return { [PlatformFilterOperator.NOT_EQUAL]: null };
    case FilterOperator.INTERVAL_WITHIN_LAST:
    case FilterOperator.INTERVAL_WITHIN_NEXT:
    case FilterOperator.INTERVAL_AFTER:
    case FilterOperator.INTERVAL_BEFORE:
      return getIntervalCondition(condition);
    // could be under `default` case, but let's make it explicit
    case FilterOperator.EQUALS:
    case FilterOperator.NOT_EQUAL:
    case FilterOperator.LESS_THAN:
    case FilterOperator.GREATER_THAN:
    case FilterOperator.LESS_THAN_OR_EQUAL:
    case FilterOperator.GREATER_THAN_OR_EQUAL:
    case FilterOperator.CONTAINS:
    case FilterOperator.NOT_CONTAINS:
    case FilterOperator.STARTS_WITH:
    case FilterOperator.DOES_NOT_START_WITH:
    case FilterOperator.ENDS_WITH:
    case FilterOperator.DOES_NOT_END_WITH:
    case FilterOperator.NOT_IN:
    case FilterOperator.NONE_OF: {
      const platformOperator = filterOperatorToPlatformFilterOperatorMap[condition.operator];
      invariant(
        platformOperator,
        `Failed to convert "${condition.operator}" into platform operator`
      );
      return { [platformOperator]: condition.value };
    }
  }
};

export const convertSimpleConditionToPlatformFilterCondition = (
  condition: SimpleCondition,
  fieldModel: IFieldModel,
  platformFilterModelRoot: PlatformFilterModel
): PlatformFilterCondition | undefined => {
  const { field, fieldEntityType } = getFieldByFilterName(condition.fieldFilterName, fieldModel);

  invariant(field, `Invalid field name in filter model: ${condition.fieldFilterName}`);

  let platformCondition = field.convertToPlatformCondition(condition);

  if (fieldEntityType) {
    invariant(
      Object.keys(platformCondition).length === 1,
      "Platform condition should have exactly one key"
    );
    invariant(
      isSimplePlatformCondition(platformCondition),
      "Platform condition is a simple condition"
    );
    const platformFieldName = Object.keys(platformCondition)[0];
    platformCondition = {
      [`${fieldEntityType}.${platformFieldName}`]: platformCondition[platformFieldName],
    };
  }

  if (isMetaField(field)) {
    // meta-fields put their values right into the root of the platformFilterModel
    Object.assign(platformFilterModelRoot, platformCondition);
    return undefined;
  }
  return platformCondition;
};

export const convertCombinedConditionToPlatformFilterCondition = (
  condition: CombinedCondition,
  fieldModel: IFieldModel,
  platformFilterModelRoot: PlatformFilterModel
): PlatformCombinedCondition | undefined => {
  const resultingConditions: PlatformFilterCondition[] = [];

  condition.conditions.forEach((condition) => {
    const fn = isCombinedCondition(condition)
      ? convertCombinedConditionToPlatformFilterCondition
      : convertSimpleConditionToPlatformFilterCondition;
    /**
     * We know that the condition argument is suitable for the given fn
     */
    /** @ts-ignore */
    const filterCondition = fn(condition, fieldModel, platformFilterModelRoot);
    if (filterCondition) {
      resultingConditions.push(filterCondition);
    }
  });

  if (!resultingConditions.length) {
    return undefined;
  }
  return {
    [filterCombineOperatorToPlatformCombineOperator[condition.operator]]: resultingConditions,
  };
};

export const convertToPlatformFilterModel = (
  filterModel: FilterModel,
  columnModel: ColumnModel,
  fieldModel: IFieldModel,
  fetchAllColumns: boolean = true
): PlatformFilterModel => {
  // First, include any special column-specific filters
  // Like `includeCustomFields`, `includeGroups`, `includeNotes`, etc.
  const platformFilterModel: PlatformFilterModel = columnModel.reduce<PlatformFilterModel>(
    (result, column) =>
      Object.assign(
        result,
        fetchAllColumns || column.visible ? column.field.extraPlatformFilters : {}
      ),
    {}
  );

  const result = convertCombinedConditionToPlatformFilterCondition(
    filterModel,
    fieldModel,
    platformFilterModel
  );

  if (result) {
    Object.assign(platformFilterModel, result);
  }

  return platformFilterModel;
};
